import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import AvailabilityViewModel from '@app/models/availability-view-model';
import BookingTierDay from '@app/models/booking-tier-day';
import FutureAvailability from '@app/models/future-availability';
import TrackedEvent from '@app/models/tracked-event';
import { format, parse, parseISO } from 'date-fns';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';

import { DateService, LogService, UserService, ValueService, DeviceService } from '@oa/shared-core';
import { Ga4Service } from './ga4.service';


@Injectable()
export class AvailabilityService {
  private readonly _serviceName = 'AvailabilityService';

  /**
   * Timeout Value in milliseconds
   */
  private readonly _timeoutMilliseconds = 30000;

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private valueService: ValueService,
    private log: LogService,
    private device: DeviceService,
    private userService: UserService,
    private ga4: Ga4Service
  ) {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Fires whenever a listing id has its availablility affected.
   */
  public availabilityChanged = new Subject<number>();

  /**
   * Get scouting day availability for a listing and a date
   * api/listing/0/scouting?date=date
   */
  public getScoutingDayAvailability(listingId: number, date: Date): Observable<AvailabilityViewModel[]> {
    const url = `${mapApiPublicRoute(listingId)}/scouting?date=${date}`;
    return this.http.get<AvailabilityViewModel[]>(url).pipe(
      map((data) => {
        // convert the string version of the date into a Date object
        data.forEach((d) => (d.date = this.dateService.parseDate(d.date)));
        return data;
      })
    );
  }

  /**
   * Get availailities for a listing/date-range
   * @param listingId
   * @param from
   * @param to
   * @param memberCount
   * @param onlyReturnAvailable
   * @param reservationStartDate
   * @param bookingType
   * @param entityId
   * @returns
   */
  public get(
    listingId: number,
    from: Date,
    to: Date,
    memberCount?: number,
    onlyReturnAvailable?: boolean,
    reservationStartDate?: Date,
    bookingType?: string,
    entityId?: number
  ): Observable<AvailabilityViewModel[]> {
    if (!bookingType) {
      bookingType = 'Daily';
    }

    if (memberCount === undefined || memberCount < 1) {
      memberCount = 1;
    }

    if (onlyReturnAvailable === undefined) {
      onlyReturnAvailable = false;
    }

    let reservationStartDateParam = '';
    if (reservationStartDate) {
      const parsedDate = typeof reservationStartDate === 'string' ? parse(reservationStartDate, 'MM/dd/yyyy', new Date()) : reservationStartDate;
      const formattedDate = format(parsedDate, 'yyyy-MM-dd');
      reservationStartDateParam = '&reservationStartDate=' + formattedDate;
    }

    let url = `${mapApiPublicRoute(listingId)}/${format(from, 'yyyy-MM-dd')}/${format(to, 'yyyy-MM-dd')}/${memberCount}?onlyReturnAvailable=${onlyReturnAvailable}${reservationStartDateParam}&bookingType=${bookingType}`;

    if (entityId && entityId > 0) {
      url = `${url}&id=${entityId}`;
    }

    if (this.verboseLogging) {
      console.log(`${this._serviceName}.get  url=`, url);
    }

    const requestStart = this.dateService.nowUTC;
    return this.http.get<AvailabilityViewModel[]>(url).pipe(
      timeout(this._timeoutMilliseconds),
      catchError((err) => {
        let trackedEvent: TrackedEvent = {
          tierName: 'Presentation',
          component: this._serviceName,
          userAccountId: this.userService.userId,
          createdOn: this.dateService.nowUTC,
          category: 'click',
          description: `Error Received ${this._serviceName}.get: ${err}`,
          jsonData: JSON.stringify({
            error: err,
            timeoutMilliseconds: this._timeoutMilliseconds,
            requestStart,
            requestEnd: this.dateService.nowUTC,
            listingId,
            from,
            to,
            memberCount,
            onlyReturnAvailable,
            reservationStartDate,
            bookingType,
            entityId,
            deviceInfo: this.device.getDeviceInfo(),
          }),
        } as TrackedEvent;

        this.log.addTrackedEvent(trackedEvent).subscribe();

        // GA4: record the availability failure as a funnel event (a concrete abandonment cause)
        this.ga4.availabilityFailed(listingId, String(err), (bookingType || '').toLowerCase());

        return throwError('Timeout has occurred');
      }),
      tap((data: any) =>
        this.ga4.availabilityChecked(listingId, Array.isArray(data) ? data.length : 0, (bookingType || '').toLowerCase())
      ),
      map((data) => {
        if (this.verboseLogging) {
          console.log(`${this._serviceName}.Get() begin mapping...`);
        }
        // convert the string version of the date into a Date object
        data.forEach((d) => {
          d.date = this.dateService.parseDate(d.date);

          d.availability.date = this.dateService.parseDate(d.date);

          if (this.dateService.isPast(d.date)) {
            d.statusReason = 'This date is in the past.';
          }
        });
        if (this.verboseLogging) {
          if (this.verboseLogging) {
            console.log(`${this._serviceName}.Get() mapping complete.`);
          }
        }
        return data;
      })
    );
  }

  getForLandowner(listingId: number, from: Date, to: Date): Observable<AvailabilityViewModel[]> {
    return this.http
      .get<AvailabilityViewModel[]>(
        `${mapApiRoute(listingId)}/landowner/${format(from, 'yyyy-MM-dd')}/${format(to, 'yyyy-MM-dd')}`
      )
      .pipe(
        map((data) => {
          // convert the string version of the date into a Date object
          data.forEach((d) => {
            d.date = this.dateService.parseDate(d.date);

            d.availability.date = this.dateService.parseDate(d.date);

            if (this.dateService.isPast(d.date)) {
              d.statusReason = 'This date is in the past.';
            }
          });

          return data;
        })
      );
  }

  set(listingId: number, status: number, dates: Date[]) {
    const formattedDates = dates.map((d) => format(d, 'yyyy-MM-dd'));
    return this.http.post(`${mapApiRoute(listingId)}/${status}`, formattedDates);
  }

  /**
   * Sets the weekend holiday booking tier to the actual days in the booking tier/listing xref
   * @param listingId
   */
  public setWeekendHolidayAsync(listingId: number) {
    if (!listingId) {
      throw "'listingId'  cannot be null/empty";
    }

    return this.http.post(`${mapApiRoute(listingId)}/weekend-holiday`, null);
  }

  /**
   * Initializes the availability cache for a listing at the API level.
   * @param listingId 
   * @returns 
   */
  public initializeAvailabilityCacheAsync(listingId: number) {
    if (!listingId) {
      return throwError("'listingId'  cannot be null/empty");      
    }
    
    return this.http.post(`${mapApiPublicRoute(listingId)}/cache/init`, null);
  }
  


  // api/listing/{listingId}/availability/weekend-holiday

  /**
   * Checks AvailabilityViewModel to see if it has any permits booked
   * @param availability
   */
  public hasPermitBookings(availability: AvailabilityViewModel) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.hasPermitBookings`);
    }

    if (availability && availability.bookings) {
      if (availability.bookings.some((x) => this.valueService.equalsIgnoringCase(x.bookingType, 'permit'))) {
        return true;
      } else {
        return false;
      }
    }

    return false;
  }

  /**
   * Gets future availability
   */
  public getFutureAvailability(listingId: number) {
    let url = `${mapApiRoute(listingId)}/future`;
    return this.http.get<FutureAvailability[]>(url);
  }

  /**
   * Gets booking tier days for a listing
   */
  public getBookingTierDays(listingId: number, startDate: Date, endDate: Date) {
    const start = this.dateService.formatDateYYYYMMDD(startDate);
    const end = this.dateService.formatDateYYYYMMDD(endDate);
    let url = `${mapApiPublicRoute(listingId)}/booking-tier/days/${start}/${end}`;
    return this.http.get<BookingTierDay[]>(url);
  }
}

/**
 * gets a partial api path (listing/${listingId}/availability)
 * @param listingId
 */
function mapApiRoute(listingId?: number) {
  return `listing/${listingId}/availability`;
}

function mapApiPublicRoute(listingId?: number) {
  return `listing/${listingId}/availability`;
}
